import React, {useMemo, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Menu} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {useDisclosure} from '@mantine/hooks';
import fileDownload from 'js-file-download';
import type {MRT_ColumnDef, MRT_RowSelectionState} from 'mantine-react-table';
import type {
  CourseAssignmentSimpleResponse,
  CourseDefinitionResponse,
} from 'src/api/ProactApiClient';
import {
  BatchCourseAssignmentUpdateTypeEnum,
  CourseAssignmentStateEnum,
  ModuleEnum,
  PermissionOperationEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useBatchUpdateCourseAssignmentsMutation,
  useExportCourseAssignmentsMutation,
} from 'src/redux/queries/courseAssignment';
import {useRecalculateCourseAssignmentRulesMutation} from 'src/redux/queries/courseAssignmentRule';
import {Pill} from 'src/design/atoms/Pill';
import {
  CourseAssignmentEnrollmentPill,
  CourseAssignmentStatePill,
  CourseAssignmentTypePill,
  UserStatePills,
  UserTypePills,
} from 'src/shared/constants/PillConstants';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {
  AlertIcon,
  RefreshIcon,
  WarningIcon,
} from 'src/design/atoms/icons/CommonIcons';
import {Spinner} from 'src/design/atoms/Spinner';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  CourseAssignmentEnrollmentDataOptions,
  CourseAssignmentStateDataOptions,
  CourseAssignmentTypeDataOptions,
  UserStateDataOptions,
  UserTypeDataOptions,
} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {DepartmentSelectBox} from 'src/app/databound-elements/DepartmentSelectBox';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {useGetCourseAssignmentListV2Query} from 'src/redux/queries/courseAssignment';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ViewCourseDefinitionAssignmentsProps {
  course: CourseDefinitionResponse;
}
const ViewCourseDefinitionAssignments: React.FC<
  ViewCourseDefinitionAssignmentsProps
> = ({course}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const trainingPerms = useModulePermissions(ModuleEnum.Training);

  const [batchUpdateCourseAssignments] =
    useBatchUpdateCourseAssignmentsMutation();
  const [exportCourseAssignments] = useExportCourseAssignmentsMutation();
  const [recalculateAssignments, {isLoading: isRefreshLoading}] =
    useRecalculateCourseAssignmentRulesMutation();

  const columns = useMemo<MRT_ColumnDef<CourseAssignmentSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'assignedTo.avatarUrl',
        header: '',
        enableColumnFilterModes: false,
        enableColumnFilter: false,
        enableSorting: false,
        enableClickToCopy: false,
        enableColumnActions: false,
        size: 50,
        enableResizing: false,
        Cell: ({cell}) => (
          <ProfileAvatar alt={'Avatar Image'} src={cell.getValue<string>()} />
        ),
      },
      {
        accessorKey: 'assignedTo.name',
        header: 'Assignee',
        enableResizing: false,
      },
      {accessorKey: 'assignedTo.employeeId', header: 'Employee ID'},
      {
        accessorKey: 'assignedTo.userType',
        header: 'User Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserTypeDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserTypePills(cell.getValue<UserTypeEnum>())} />
        ),
      },
      {
        accessorKey: 'assignedTo.vendor.name',
        header: 'Vendor',
      },
      {
        accessorKey: 'assignedTo.active',
        header: 'User Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserStatePills(cell.getValue<UserStatusEnum>())} />
        ),
      },
      {
        accessorKey: 'state',
        header: 'Training Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill
            {...CourseAssignmentStatePill(
              cell.getValue<CourseAssignmentStateEnum>()
            )}
          />
        ),
      },
      {
        accessorKey: 'isRenewal',
        header: 'Assignment Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentTypeDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...CourseAssignmentTypePill(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'active',
        header: 'Enrollment',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: CourseAssignmentEnrollmentDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...CourseAssignmentEnrollmentPill(cell.getValue<boolean>())} />
        ),
      },
      {
        id: 'assignedAt',
        accessorFn: row => (row.assignedAt ? new Date(row.assignedAt) : null),
        header: 'Assigned Date',
        filterVariant: 'date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        id: 'completedAt',
        accessorFn: row => (row.completedAt ? new Date(row.completedAt) : null),
        header: 'Complete Date',
        filterVariant: 'date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        id: 'expectedExpirationAt',
        accessorFn: row =>
          row.expectedExpirationAt ? new Date(row.expectedExpirationAt) : null,
        header: 'Renew Date',
        filterVariant: 'date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        id: 'dueAt',
        accessorFn: row => (row.dueAt ? new Date(row.dueAt) : null),
        header: 'Due Date',
        filterVariant: 'date',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | undefined | null>();
          if (value && new Date(value) < new Date()) {
            return <Box c="red">{value?.toString().toShortDate()}</Box>;
          } else {
            return <>{value && value?.toString().toShortDate()}</>;
          }
        },
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'course-def-assignments',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'assignedTo.active',
        value: [
          UserStatusEnum.Active.toString(),
          UserStatusEnum.Pending.toString(),
        ],
      },
      {
        id: 'active',
        value: ['true', 'false'],
      },
    ],
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'assignedTo.name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const [filterSupervisorId, setFilterSupervisorId] = useState<
    string | undefined
  >();
  const [filterDepartmentId, setFilterDepartmentId] = useState<
    string | undefined
  >();

  const [isBulkActionSaving, setIsBulkActionSaving] =
    useState<BatchCourseAssignmentUpdateTypeEnum>(
      BatchCourseAssignmentUpdateTypeEnum.Invalid
    );

  const [isExportLoading, setIsExportLoading] = useState(false);

  const [pendingBulkAction, setPendingBulkAction] = useState<
    BatchCourseAssignmentUpdateTypeEnum | undefined
  >(undefined);
  const [confirmBulkActionOpened, confirmBulkActionHandlers] =
    useDisclosure(false);

  const apiRequest = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
    courseDefinitionId: course.id,
    supervisorId: filterSupervisorId ? +filterSupervisorId : undefined,
    departmentId: filterDepartmentId ? +filterDepartmentId : undefined,
    viewCurrentUserOnly: false,
  };
  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseAssignmentListV2Query(apiRequest);
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const selectedCount = Object.keys(rowSelection ?? {}).length;

  const {table} = useTable({
    ...tableState,
    rowSelection: rowSelection,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowSelection: trainingPerms.update,
      enableRowActions: true,
      columns: columns,
      data: rows,
      onRowSelectionChange: setRowSelection,
      getRowId: row => String(row.id),
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item
            onClick={() =>
              navigate(
                PathUtility.TrainingCourseAssignmentView(
                  String(course.id),
                  String(row.original.id),
                  'content'
                )
              )
            }>
            View Assignment
          </Menu.Item>

          <PermissionGuard
            module={ModuleEnum.Personnel}
            operation={PermissionOperationEnum.View}>
            <Menu.Item
              onClick={() =>
                window.open(
                  PathUtility.PersonnelView(
                    String(row.original.assignedTo?.id),
                    'training'
                  ),
                  '_blank'
                )
              }>
              View Personnel (Opens in new tab)
            </Menu.Item>
          </PermissionGuard>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {trainingPerms.update && (
              <>
                <TableToolbar.Action
                  onClick={() =>
                    requestBulkAction(
                      BatchCourseAssignmentUpdateTypeEnum.Reopen
                    )
                  }
                  variant="secondary"
                  icon={<WarningIcon />}
                  loading={
                    isBulkActionSaving ==
                    BatchCourseAssignmentUpdateTypeEnum.Reopen
                  }
                  disabled={
                    course.locked ||
                    selectedCount == 0 ||
                    isBulkActionSaving !=
                      BatchCourseAssignmentUpdateTypeEnum.Invalid
                  }>{`Re-Open ${selectedCount}`}</TableToolbar.Action>
                <TableToolbar.Action
                  onClick={() =>
                    requestBulkAction(
                      BatchCourseAssignmentUpdateTypeEnum.Complete
                    )
                  }
                  variant="danger"
                  icon={<AlertIcon />}
                  loading={
                    isBulkActionSaving ==
                    BatchCourseAssignmentUpdateTypeEnum.Complete
                  }
                  disabled={
                    course.locked ||
                    selectedCount == 0 ||
                    isBulkActionSaving !=
                      BatchCourseAssignmentUpdateTypeEnum.Invalid
                  }>{`Complete ${selectedCount}`}</TableToolbar.Action>
                <TableToolbar.Action
                  onClick={handleRefreshAssignments}
                  variant="secondary"
                  icon={<RefreshIcon />}
                  loading={isRefreshLoading}
                  disabled={isRefreshLoading}
                  tooltip="Re-apply assignment rules now using current groups, departments, and supervisors">
                  Refresh Assignments
                </TableToolbar.Action>
              </>
            )}
            {trainingPerms.export && (
              <TableToolbar.Action
                variant="export"
                onClick={handleExportAssignments}
                loading={isExportLoading}
                disabled={course.locked || isExportLoading}
              />
            )}
          </TableToolbar.Actions>
          <TableToolbar.Filters>
            <Box style={{width: '250px'}}>
              <PersonSelectBox
                queryOptions={{
                  superviserOnly: true,
                  userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
                  active: [UserStatusEnum.Active],
                }}
                placeholder="Select Supervisor to filter"
                value={filterSupervisorId}
                onSelectChangeCallback={handleSupervisorIdChange}
              />
            </Box>
            <Box style={{width: '250px'}}>
              <DepartmentSelectBox
                queryOptions={{}}
                value={filterDepartmentId}
                onSelectChangeCallback={handleDepartmentIdChange}
                placeholder="Select department to filter"
              />
            </Box>
          </TableToolbar.Filters>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const requestBulkAction = (type: BatchCourseAssignmentUpdateTypeEnum) => {
    if (selectedCount === 0) {
      return;
    }

    setPendingBulkAction(type);
    confirmBulkActionHandlers.open();
  };

  const handleBulkAction = async () => {
    const type = pendingBulkAction;
    confirmBulkActionHandlers.close();

    if (type === undefined || selectedCount === 0) {
      return;
    }

    try {
      setIsBulkActionSaving(type);

      const selectedRows = rows.filter(row => rowSelection[String(row.id)]);
      const ids = selectedRows.map(row => row.id!);
      const expectedCount = ids.length;

      const count = await batchUpdateCourseAssignments({
        updateType: type,
        ids: ids,
      }).unwrap();

      if (expectedCount == count) {
        setAlert(`Successfully updated ${count} items.`, 'success');
      } else {
        const targetState =
          type === BatchCourseAssignmentUpdateTypeEnum.Reopen
            ? CourseAssignmentStateEnum.Open
            : CourseAssignmentStateEnum.Complete;

        const refreshed = await refetch();
        const freshRows = refreshed.data?.data ?? [];

        const failedNames = selectedRows
          .filter(row => {
            const fresh = freshRows.find(freshRow => freshRow.id === row.id);
            return !fresh || fresh.state !== targetState;
          })
          .map(row => row.assignedTo?.name ?? `Assignment #${row.id}`);

        setAlert(
          failedNames.length > 0
            ? `Updated ${count} of ${expectedCount} items. Failed: ${failedNames.join(
                ', '
              )}.`
            : `Successfully updated ${count} items.`,
          failedNames.length > 0 ? 'error' : 'success'
        );
      }

      setRowSelection({});
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsBulkActionSaving(BatchCourseAssignmentUpdateTypeEnum.Invalid);
    setPendingBulkAction(undefined);
  };

  const handleRefreshAssignments = async () => {
    if (!course.id) {
      return;
    }

    try {
      await recalculateAssignments(course.id).unwrap();
      setAlert(
        'Refresh started. Assignments update in the background; refresh the table in a few minutes to see changes.',
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleExportAssignments = async () => {
    try {
      if (!course.id || !course.version) {
        return;
      }

      setIsExportLoading(true);

      const response = await exportCourseAssignments(apiRequest).unwrap();

      fileDownload(
        response!.data,
        `training_export_${new Date().toString().toShortDate()}.csv`
      );
      setAlert(`Successfully exported file.`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsExportLoading(false);
  };

  function handleSupervisorIdChange(value: ISelectOption | null): void {
    setFilterSupervisorId(String(value?.id));
  }
  function handleDepartmentIdChange(value: ISelectOption | null): void {
    setFilterDepartmentId(String(value?.id));
  }

  const isReopenAction =
    pendingBulkAction === BatchCourseAssignmentUpdateTypeEnum.Reopen;

  return !isLoading && course ? (
    <>
      {table}
      <ConfirmDialog
        opened={confirmBulkActionOpened}
        onClose={() => {
          confirmBulkActionHandlers.close();
          setPendingBulkAction(undefined);
        }}
        onConfirm={handleBulkAction}
        confirmationButtonColor={isReopenAction ? 'yellow' : 'red'}
        title={
          isReopenAction
            ? `Re-Open ${selectedCount} assignment${
                selectedCount === 1 ? '' : 's'
              }?`
            : `Mark ${selectedCount} assignment${
                selectedCount === 1 ? '' : 's'
              } complete?`
        }
        body={
          isReopenAction
            ? `This will reopen ${selectedCount} training assignment${
                selectedCount === 1 ? '' : 's'
              }, marking ${
                selectedCount === 1 ? 'it' : 'them'
              } incomplete again.`
            : `This will mark ${selectedCount} training assignment${
                selectedCount === 1 ? '' : 's'
              } as complete, even if the trainee has not actually finished the course. This falsifies training records and cannot be undone.`
        }
      />
    </>
  ) : (
    <Spinner loadingText="Loading...." />
  );
};

export default ViewCourseDefinitionAssignments;
